import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import React, { useId, useState } from 'react';

import { StructureZoneBannerProps } from './StructureZoneBanner.props';
import { useStructureZoneSuggestions } from './useStructureZoneSuggestions';
import { v4GhostButtonSx } from '../../../components/atoms/Button';
import { TablePill } from '../../../components/atoms/TablePill';
import { COLORS } from '../../../configs/colors';

const lineSx = { fontSize: '12px', lineHeight: 1.5, color: COLORS.TagAmberText };

/**
 * Says a structure has no zone, in one line, inside the Address panel.
 *
 * Zones are derived, not entered: `api-geosocial` matches the address against
 * its region table, so "no zone" almost always means one word — the suburb — is
 * not in that table. This used to be a page-wide amber block listing a dozen
 * suburbs above the address, which is a lot of furniture for a one-word problem
 * and pushed the address itself below the fold.
 *
 * So it is framed as a spelling correction instead, an idiom the reader already
 * knows: the suburb is marked in the address line above (see
 * `AddressContactRow`'s `unmatchedTerm`), and this offers the dictionary. Each
 * suggestion is a button, and picking one writes it straight onto the address —
 * the correction is a click, not a re-typing exercise, and the header's unsaved
 * pill then asks for the Save.
 */
export const StructureZoneBanner: React.FC<StructureZoneBannerProps> = ({
  suburb,
  postcode,
  state,
  country,
  onApplySuburb,
}) => {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const { isLoading, suggestions, postcodeUnknown, lookupFailed, isTruncated } =
    useStructureZoneSuggestions({ postcode, state, country });

  const headline = !postcode
    ? 'No zone — this address has no postcode.'
    : suburb
      ? `No zone — “${suburb}” is not a known suburb for ${postcode}.`
      : `No zone — nothing on this address matches a known region.`;

  const detail = !postcode
    ? 'Add a postcode and save; the zone is matched from it.'
    : postcodeUnknown
      ? `No region is set up for ${postcode} at all. The postcode may be wrong, or it needs adding under Configure › Geosocial › Regions.`
      : lookupFailed
        ? `Could not check what ${postcode} is known as. Try again, or check Configure › Geosocial.`
        : undefined;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: COLORS.TagAmberText,
        borderRadius: '6px',
        bgcolor: COLORS.TagAmberBg,
        px: 1.25,
        py: 1,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <WarningAmberIcon sx={{ fontSize: 16, color: COLORS.TagAmberText, flexShrink: 0 }} />
        <Typography sx={{ ...lineSx, fontWeight: 600, flex: 1, minWidth: 0 }}>
          {headline}
        </Typography>

        {isLoading && <CircularProgress size={13} sx={{ color: COLORS.TagAmberText }} />}

        {suggestions.length > 0 && (
          <MuiButton
            variant="outlined"
            size="small"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            sx={{
              ...v4GhostButtonSx,
              color: COLORS.TagAmberText,
              borderColor: COLORS.TagAmberText,
              '&:hover': {
                borderColor: COLORS.TagAmberText,
                color: COLORS.TagAmberText,
                bgcolor: 'transparent',
              },
            }}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}
              />
            }
          >
            {onApplySuburb ? 'Pick the right suburb' : 'See known suburbs'} ({suggestions.length})
          </MuiButton>
        )}
      </Box>

      {detail && <Typography sx={{ ...lineSx, mt: 0.75 }}>{detail}</Typography>}

      {/* The id lives on Collapse, which stays mounted, so the disclosure's
          aria-controls never points at a missing element. Collapse keeps the
          collapsed body out of the accessibility tree itself (height 0 +
          visibility hidden), so `unmountOnExit` is not needed for that. */}
      <Collapse in={open} id={listId}>
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: COLORS.TagAmberText }}>
          <Typography sx={{ ...lineSx, mb: 0.75 }}>
            {onApplySuburb
              ? `Postcode ${postcode} is known by these. Pick one to put it on the address.`
              : `Postcode ${postcode} is known by these.`}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 0.5,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {suggestions.map(({ locality, geoName }) =>
              onApplySuburb ? (
                <Box
                  component="button"
                  type="button"
                  key={`${locality}-${geoName}`}
                  onClick={() => {
                    onApplySuburb(locality);
                    setOpen(false);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                    px: 1,
                    py: '5px',
                    border: 1,
                    borderColor: 'transparent',
                    borderRadius: '4px',
                    bgcolor: 'transparent',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'background.paper', borderColor: COLORS.TagAmberText },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: COLORS.TagAmberText,
                      outlineOffset: 1,
                    },
                  }}
                >
                  <Typography sx={{ ...lineSx, fontWeight: 600 }}>{locality}</Typography>
                  <TablePill tone="info">{geoName}</TablePill>
                </Box>
              ) : (
                <Box
                  key={`${locality}-${geoName}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: '5px' }}
                >
                  <Typography sx={{ ...lineSx, fontWeight: 600 }}>{locality}</Typography>
                  <TablePill tone="info">{geoName}</TablePill>
                </Box>
              )
            )}
          </Box>

          {isTruncated && (
            <Typography sx={{ ...lineSx, mt: 0.75, fontStyle: 'italic' }}>
              More localities exist for this postcode than are shown here.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
